import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { IPost, IPostCreate, Params, ActionType, Discipline } from "../models";
import {
  AssetsV1Service,
  POIsV1Service,
  PostsV2Service,
  ShapeFilesV1Service,
} from "../../../common/services/api/api";
import { Loading } from "../../../common/components/loading/loading";
import { useForm } from "react-hook-form";
import Context from "../../../common/components/utils/context";
import { Location, FormData } from "./models";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import { Circles } from "react-loader-spinner";
import Select from "react-select";
import { IContact } from "../../../common/models/accounts";
import { Themes } from "../../../common/enums/themes";
import { PostEditMap } from "./postEditMap";
import { useLocation } from "react-router-dom";
import { IShapeFile } from "../../earthtwin/models";
import { IPOI } from "../../matterportViewer/matterportViewer3";
import { IAsset } from "../../admin/models";

export const PostEdit = () => {
  const stateLocation = useLocation();
  let navigate = useNavigate();

  const context = useContext(Context);
  const uploadRef = useRef<HTMLLabelElement>(null);

  const [isEdit, setIsEdit] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [post, setPost] = useState<IPost | null>(null);
  const [imageBase64, setImageBase64] = useState<string>("");
  const [location, setLocation] = useState<Location>({
    longitude: 0,
    latitude: 0,
    altitude: 0,
  });
  const [shareWith, setShareWith] = useState("Company");
  const [contacts, setContacts] = useState<IContact[]>([]);
  const [selectedUser, setSelectedUser] = useState("");
  const [shapeFile, setShapeFile] = useState<IShapeFile>();
  const [poiFile, setPOIFile] = useState<IPOI>();
  const [asset, setAsset] = useState<IAsset>();

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };

  const { id } = useParams<{ id: string }>();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState,
    formState: { errors },
  } = useForm<FormData>({
    mode: "onChange",
    defaultValues: {
      discipline: Discipline.General,
      objectType: "Cube",
      billboard: true,
      displayType: "3D",
      status: "Company",
    },
  });

  const { isSubmitting } = formState;

  useEffect(() => {
    if (context.account.relations != null) {
      const idsRelations = context.account.relations.map(
        ({ accountId }) => accountId
      );
      const filteredContacts = context.account.relations.filter(
        ({ accountId }, index) => !idsRelations.includes(accountId, index + 1)
      );
      setContacts(
        filteredContacts.sort((a, b) => a.username.localeCompare(b.username))
      );
    }
  }, [context.account.relations]);

  useEffect(() => {
    if (context.account.accountId != null) {
      const fetchPost = async () => {
        try {
          setLoading(true);
          const postService = new PostsV2Service();
          if (id) {
            setIsEdit(true);
            var response = await postService.get(id);
            if (response.data.accountId !== context.account.accountId) {
              navigate(`/posts`);
            } else {
              setPost(response.data);
            }
          }
        } catch (error) {
          console.error("Error fetching post:", error);
        } finally {
          setLoading(false);
        }
      };

      fetchPost();
    }
  }, [context, id]);

  useEffect(() => {
    if (post === null) return;

    const data = mapPostToFormData(post);
    reset(data);

    setLocation({
      longitude: post.longitude,
      latitude: post.latitude,
      altitude: post.altitude,
    });

    if (post.shapeFileId) {
      getShapeFile(post.shapeFileId);
    }

    if (post.poiFileId) {
      getPOIFile(post.poiFileId);
    }
  }, [post]);

  const getShapeFile = async (id: string) => {
    try {
      const shapeFileService = new ShapeFilesV1Service();
      const response = await shapeFileService.get(id);
      setShapeFile(response.data);
    } catch (error) {
      console.error("Error fetching shape file:", error);
    }
  };

  const getPOIFile = async (id: string) => {
    try {
      const poiService = new POIsV1Service();
      const response = await poiService.get(id);
      setPOIFile(response.data);
    } catch (error) {
      console.error("Error fetching POI file:", error);
    }
  };

  useEffect(() => {
    if (stateLocation.state?.shapeFile) {
      setShapeFile(stateLocation.state.shapeFile);
    }
  }, [stateLocation.state?.shapeFile]);

  useEffect(() => {
    if (stateLocation.state?.poiFile) {
      setPOIFile(stateLocation.state.poiFile);
    }
  }, [stateLocation.state?.poiFile]);

  useEffect(() => {
    if (stateLocation.state?.asset) {
      setAsset(stateLocation.state?.asset);

      console.log("Mobile asset from state:", stateLocation.state?.asset);
    }
  }, [stateLocation.state?.asset]);

  const mapPostToFormData = (post: IPost): FormData => {
    let params: Params = {
      actions: [],
    };

    if (post.params != "") {
      params = JSON.parse(post.params);
    }

    return {
      body: post.body,
      discipline: post.discipline as Discipline,
      longitude: post.longitude,
      latitude: post.latitude,
      altitude: post.altitude,
      seaLevel: post.seaLevel,
      pictureBase64: post.pictureURL,
      objectType: params.actions.find(
        (action) => action.type === ActionType.OBJECT3D
      )?.value as string,
      url: params.actions.find((action) => action.type === ActionType.URL)
        ?.value as string,
      billboard: params.actions.find(
        (action) => action.type === ActionType.BILLBOARD
      )?.value as boolean,
      displayType: params.actions.find(
        (action) => action.type === ActionType.DISPLAY
      )?.value as string,
      status: post.status,
    };
  };

  const onSelectObject = (
    longitude: number,
    latitude: number,
    altitude: number,
    seaLevel: number
  ) => {
    setValue("longitude", longitude, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("latitude", latitude, { shouldDirty: true, shouldValidate: true });
    setValue("altitude", altitude, { shouldDirty: true, shouldValidate: true });
    setValue("seaLevel", seaLevel, { shouldDirty: true, shouldValidate: true });
  };

  // Function to handle image file input and convert to base64
  const handleImageUpload = (event: { target: { files: any[] } }) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (reader.result) {
          setImageBase64(reader.result.toString()); // Set the base64 string to state
        }
      };
      reader.readAsDataURL(file); // Convert image to Base64 string
    }
  };

  const transformToFullURL = (link: string) => {
    const hasProtocol =
      link.startsWith("http://") || link.startsWith("https://");
    const hasDomain = link.includes(".") || link === "localhost";
    if (hasProtocol && hasDomain) {
      return link;
    } else if (hasDomain) {
      return "https://" + link;
    } else {
      return "";
    }
  };

  const onSubmit = async (data: FormData) => {
    data.pictureBase64 = imageBase64.replace(
      /^data:image\/[a-zA-Z]+;base64,/,
      ""
    );

    const postUrl = data.url.trim();
    data.url = postUrl ? transformToFullURL(postUrl) : "";

    let sharing = [];
    if (selectedUser) {
      sharing.push(selectedUser);
    }
    //  if (selectedGroup) {
    //    sharing.push(selectedGroup);
    //  }
    //  if (selectedAllContacts) {
    //    sharing = selectedAllContacts;
    //  }

    const params: Params = {
      actions: [
        {
          type: ActionType.URL,
          value: data.url,
        },
        {
          type: ActionType.DISPLAY,
          value: data.displayType,
        },
        {
          type: ActionType.OBJECT3D,
          value: data.objectType,
        },
        {
          type: ActionType.BILLBOARD,
          value: data.billboard,
        },
      ],
    };

    const newPost: IPostCreate = {
      // id: uuidv4(),
      // accountId: context.account.accountId,
      pictureBase64: data.pictureBase64,
      altitude: data.altitude,
      seaLevel: data.seaLevel,
      body: data.body,
      discipline: data.discipline,
      latitude: data.latitude,
      longitude: data.longitude,
      params: JSON.stringify(params),
      status: data.status,
      what3words: "",
      sharedWith: sharing,
      shapeFileId: shapeFile?.id,
      poiFileId: poiFile?.id,
    };

    try {
      const postService = new PostsV2Service();
      if (!isEdit) {
        const result = await postService.create(newPost);
        if (result.data.id && asset) {
          const assetService = new AssetsV1Service();
          await assetService.assignPostToAsset(result.data.id, asset.id);
        }
        context.setAlert({
          message: "Post was successfully created!",
          theme: Themes.Success,
        });
      } else if (post != null) {
        post.altitude = data.altitude;
        post.seaLevel = data.seaLevel;
        post.body = data.body;
        post.discipline = data.discipline;
        post.latitude = data.latitude;
        post.longitude = data.longitude;
        post.params = JSON.stringify(params);
        post.status = data.status;
        post.sharedWith = sharing;

        const result = await postService.update(post);
        context.setAlert({
          message: "Post was successfully updated!",
          theme: Themes.Success,
        });
      }

      navigate(`/posts`);
    } catch (error) {
      console.error("Error creating post:", error);
      context.setAlert({
        message:
          "Error creating post: please review your entries or try another image",
        theme: Themes.Danger,
      });
    }
  };

  const handleLocationChange = () => {
    setLocation({
      longitude: getValues("longitude"),
      latitude: getValues("latitude"),
      altitude: getValues("altitude"),
    });
  };

  const handleCancelClick = () => {
    navigate(`/posts`);
  };

  const extractUrl = (text: string) => {
    if (!text) return "ok";
    const match = text.match(/https?:\/\/[^\s"']+/);
    return match ? match[0] : "";
  };

  return (
    <>
      {loading ? (
        <Loading />
      ) : isEdit && post == null ? (
        <p>Post not found</p>
      ) : (
        <div className="md:flex">
          <div className="w-screen h-2/5 md:h-screen md:w-2/3 xl:w-4/5 relative">
            <PostEditMap
              onClick={onSelectObject}
              location={location}
              shapeFile={shapeFile}
              poiFile={poiFile}
              asset={asset}
            />
          </div>
          <div className="w-screen md:w-1/3 xl:w-1/5 md:h-screen overflow-y-auto">
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="w-full h-auto mx-auto mb-4">
                {imageBase64 && (
                  <img
                    src={imageBase64.toString()}
                    alt="Uploaded Preview"
                    className="w-full h-auto object-cover cursor-pointer bg-gray-700 hover:opacity-50 transition-opacity"
                    onClick={() => uploadRef?.current?.click()}
                  />
                )}
                {isEdit && !imageBase64 && register("pictureBase64") && (
                  <img
                    src={getValues("pictureBase64")}
                    alt="Uploaded"
                    className="mt-2"
                  />
                )}
                {!imageBase64 && (
                  <fieldset className="bg-gray-300 px-5 py-10 flex flex-col justify-center">
                    {!isEdit && (
                      <div className="grid grid-cols-1 gap-4 mb-5">
                        <img
                          src="/img/AR-phone.svg"
                          alt="AR phone"
                          className="justify-self-center"
                        />
                        <h2 className="justify-self-center text-xl">
                          Create an AR post
                        </h2>
                        <p className="text-center">
                          AR posts can contain a virtual object/image, a body of
                          text and a link to an external data source
                        </p>
                      </div>
                    )}
                    <label
                      htmlFor="uploadImage"
                      className="rounded-lg text-gray-800 text-sm tracking-wider font-medium outline-none bg-text-link px-5 py-2 cursor-pointer w-1/3 mx-auto text-center"
                      ref={uploadRef}
                    >
                      Upload File
                      <input
                        type="file"
                        id="uploadImage"
                        className="hidden"
                        accept="image/*"
                        {...register("pictureBase64", {
                          required: isEdit ? false : "Please upload an image",
                          onChange: handleImageUpload, // Convert image to Base64
                        })}
                      />
                    </label>
                    {errors.pictureBase64 && (
                      <p className="text-red-500">
                        {errors.pictureBase64.message}
                      </p>
                    )}
                  </fieldset>
                )}
              </div>
              <div className="px-4 flex flex-col space-y-4">
                <fieldset>
                  <label htmlFor="longitude" className="text-gray-500 text-sm">
                    Longitude
                  </label>
                  <input
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    {...register("longitude", {
                      required: "This field is required",
                      onChange: handleLocationChange,
                    })}
                  />
                  {errors.longitude && (
                    <p className="text-red-500">{errors.longitude.message}</p>
                  )}
                </fieldset>
                <fieldset>
                  <label htmlFor="latitude" className="text-gray-500 text-sm">
                    Latitude
                  </label>
                  <input
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    {...register("latitude", {
                      required: "This field is required",
                      onChange: handleLocationChange,
                    })}
                  />
                  {errors.latitude && (
                    <p className="text-red-500">{errors.latitude.message}</p>
                  )}
                </fieldset>
                <fieldset>
                  <label htmlFor="altitude" className="text-gray-500 text-sm">
                    Altitude
                  </label>
                  <input
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    {...register("seaLevel", {
                      required: "This field is required",
                      onChange: handleLocationChange,
                    })}
                  />
                  {errors.altitude && (
                    <p className="text-red-500">{errors.altitude.message}</p>
                  )}
                </fieldset>
                <fieldset>
                  <label htmlFor="body" className="text-gray-500 text-sm">
                    Post description
                  </label>
                  <textarea
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    {...register("body")}
                  />
                  {errors.body && (
                    <p className="text-red-500">{errors.body.message}</p>
                  )}
                </fieldset>
                <fieldset>
                  <label htmlFor="status" className="text-gray-500 text-sm">
                    Share with
                  </label>
                  <select
                    id="status"
                    {...register("status")}
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    onChange={(e) => setShareWith(e.target.value)}
                  >
                    <option value="Public">Public</option>
                    <option value="Company">Company</option>
                    <option value="Group">Group</option>
                    <option value="User">User</option>
                    <option value="Me only">Me only</option>
                  </select>
                </fieldset>
                {shareWith === "User" && (
                  <Select
                    onChange={(val, i) => {
                      setSelectedUser((val?.value as string) || "");
                    }}
                    options={contacts.map((c, index) => {
                      return {
                        label: c.username,
                        value: c.accountId,
                        key: index,
                      };
                    })}
                  />
                )}
                <fieldset>
                  <label htmlFor="body" className="text-gray-500 text-sm">
                    URL
                  </label>
                  <input
                    className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                    {...register("url", {
                      required: false,
                      validate: (value) => {
                        const url = extractUrl(value);
                        if (url == "ok") return true;
                        else if (!url) return "No valid URL found";
                        setValue("url", url, { shouldValidate: false });
                        return true;
                      },
                    })}
                  />
                  {errors.url && (
                    <p className="text-red-500">{errors.url.message}</p>
                  )}
                </fieldset>
                <fieldset>
                  {/* Accordion Header */}
                  <div
                    className="cursor-pointer flex justify-between items-center mb-4 mt-8"
                    onClick={toggleAccordion}
                  >
                    <h3 className="font-medium">Additional options</h3>
                    <span>
                      {isOpen ? <IoIosArrowUp /> : <IoIosArrowDown />}
                    </span>
                  </div>
                  {/* Accordion Content */}
                  <div
                    className={`transition-max-height duration-500 ease-in-out overflow-hidden ${
                      isOpen ? "max-h-96" : "max-h-0"
                    }`}
                  >
                    <div>
                      <div className="mb-8">
                        <label
                          htmlFor="discipline"
                          className="text-gray-500 text-sm"
                        >
                          Discipline
                        </label>
                        <select
                          className="block w-full bg-transparent outline-none border-b-2 py-2 px-4"
                          id="discipline"
                          {...register("discipline", {
                            required: "Please select a discipline",
                          })}
                        >
                          <option value="">Select a discipline</option>
                          {Object.values(Discipline).map((discipline) => (
                            <option key={discipline} value={discipline}>
                              {discipline}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="mb-8">
                        <label
                          htmlFor="ObjectType"
                          className="text-gray-500 text-sm"
                        >
                          Object type
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              value="Cube"
                              {...register("objectType", {
                                required: "This field is required",
                              })}
                              className="mr-2"
                            />
                            Cube
                          </label>

                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              value="Sphere"
                              {...register("objectType")}
                              className="mr-2"
                            />
                            Sphere
                          </label>

                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              value="Polygon"
                              {...register("objectType")}
                              className="mr-2"
                            />
                            Polygon
                          </label>
                        </div>
                      </div>

                      <div className="mb-8">
                        <label
                          htmlFor="Billboard"
                          className="text-gray-500 text-sm"
                        >
                          Billboard
                        </label>
                        <div className="flex space-x-4">
                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              value="true"
                              {...register("billboard")}
                              className="mr-2"
                            />
                            Yes
                          </label>

                          <label className="flex items-center text-sm">
                            <input
                              type="radio"
                              value="false"
                              {...register("billboard")}
                              className="mr-2"
                            />
                            No
                          </label>
                        </div>
                      </div>

                      <div className="mb-8">
                        <label
                          htmlFor="displayType"
                          className="text-gray-500 text-sm"
                        >
                          Display type
                        </label>
                        <select
                          id="displayType"
                          {...register("displayType")}
                          className="block w-full bg-gray-50 outline-none border-b py-2 px-4"
                        >
                          <option value="">Please select an option</option>
                          <option value="2D">2D</option>
                          <option value="3D">3D</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>
              <div className="grid grid-flow-col justify-stretch space-x-3 px-4 mb-4">
                <button
                  type="button"
                  className="rounded-lg text-gray-400 text-sm tracking-wider font-medium outline-none bg-gray-100 hover:bg-gray-200 py-2"
                  onClick={handleCancelClick}
                >
                  Cancel
                </button>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className={`flex items-center justify-center rounded-lg text-gray-800 text-sm tracking-wider font-medium outline-none ${
                    isSubmitting ? "bg-gray-100" : "bg-text-link"
                  }`}
                >
                  {isSubmitting && (
                    <div className="mr-2">
                      <Circles height="20" width="20" color="#2DE1FC" />
                    </div>
                  )}
                  <span>{isEdit ? "Update" : "Post"}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
